var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __spreadArray = (this && this.__spreadArray) || function (to, from, pack) {
    if (pack || arguments.length === 2) for (var i = 0, l = from.length, ar; i < l; i++) {
        if (ar || !(i in from)) {
            if (!ar) ar = Array.prototype.slice.call(from, 0, i);
            ar[i] = from[i];
        }
    }
    return to.concat(ar || Array.prototype.slice.call(from));
};
// SPDX-FileCopyrightText: 2026 Sequent Tech Inc <legal@sequentech.io>
//
// SPDX-License-Identifier: AGPL-3.0-only
import { canonicalize_slates_js, check_election_slates_js } from "sequent-core";
import { ESlateProblemSeverity, SLATES_ANNOTATION, getSlateName, } from "@sequentech/ui-core";
/** The only slate configuration version the platform reads. */
export var SLATES_VERSION = 1;
/** The election form fields of the Ballot Design slate settings. */
export var SLATES_ENABLED_FIELD = "slates_enabled";
export var SLATES_MOBILE_CANDIDATE_LISTS_FIELD = "slates_mobile_candidate_lists";
var isRecord = function (value) {
    return typeof value === "object" && value !== null && !Array.isArray(value);
};
var isStringList = function (value) {
    return Array.isArray(value) && value.every(function (item) { return typeof item === "string"; });
};
var isSlate = function (value) {
    return isRecord(value) &&
        typeof value.id === "string" &&
        isRecord(value.name) &&
        Object.values(value.name).every(function (name) { return typeof name === "string"; }) &&
        isRecord(value.members) &&
        Object.values(value.members).every(isStringList);
};
var isSlatesConfig = function (value) {
    return isRecord(value) &&
        typeof value.version === "number" &&
        Array.isArray(value.slates) &&
        value.slates.every(isSlate);
};
var isProblemList = function (value) {
    return Array.isArray(value) &&
        value.every(function (item) {
            return isRecord(item) &&
                Object.values(ESlateProblemSeverity).some(function (severity) { return severity === item.severity; }) &&
                typeof item.code === "string" &&
                typeof item.path === "string" &&
                typeof item.message === "string";
        });
};
export var hasSlatesAnnotation = function (annotations) {
    return (annotations === null || annotations === void 0 ? void 0 : annotations[SLATES_ANNOTATION]) !== undefined;
};
/** The stored slate configuration, when there is one that can be read. */
export var readSlatesConfig = function (annotations) {
    var stored = annotations === null || annotations === void 0 ? void 0 : annotations[SLATES_ANNOTATION];
    if (typeof stored !== "string") {
        return undefined;
    }
    try {
        var parsed = JSON.parse(stored);
        return isSlatesConfig(parsed) ? parsed : undefined;
    }
    catch (_a) {
        return undefined;
    }
};
/**
 * The annotations with the canonical form of `config`, or without the slate
 * annotation when there is no configuration. Every other annotation is kept.
 */
export var writeSlatesConfig = function (annotations, config) {
    var result = __assign({}, (annotations !== null && annotations !== void 0 ? annotations : {}));
    if (!config) {
        delete result[SLATES_ANNOTATION];
        return result;
    }
    result[SLATES_ANNOTATION] = canonicalize_slates_js(JSON.stringify(config));
    return result;
};
/** The annotations after saving the Ballot Design slate settings. */
export var applySlatesSettings = function (annotations, settings) {
    var _a, _b;
    if (!settings.enabled) {
        return writeSlatesConfig(annotations, undefined);
    }
    var config = readSlatesConfig(annotations);
    if (!config && hasSlatesAnnotation(annotations)) {
        return __assign({}, annotations);
    }
    return writeSlatesConfig(annotations, {
        version: (_a = config === null || config === void 0 ? void 0 : config.version) !== null && _a !== void 0 ? _a : SLATES_VERSION,
        mobile_candidate_lists: settings.mobileCandidateLists,
        slates: (_b = config === null || config === void 0 ? void 0 : config.slates) !== null && _b !== void 0 ? _b : [],
    });
};
/** The configuration with `slate` in place of the one with its id, or appended. */
export var putSlate = function (config, slate) {
    var index = config.slates.findIndex(function (_a) {
        var id = _a.id;
        return id === slate.id;
    });
    var slates = index === -1
        ? __spreadArray(__spreadArray([], config.slates, true), [slate], false) : config.slates.map(function (current, position) { return (position === index ? slate : current); });
    return __assign(__assign({}, config), { slates: slates });
};
export var removeSlate = function (config, slateId) { return (__assign(__assign({}, config), { slates: config.slates.filter(function (_a) {
        var id = _a.id;
        return id !== slateId;
    }) })); };
var UNSUITABLE_MEMBER_FLAGS = [
    "is_disabled",
    "is_write_in",
    "is_explicit_blank",
    "is_explicit_invalid",
    "is_category_list",
];
/** Whether a candidate can be a slate member, as sequent-core decides it. */
export var isEligibleSlateMember = function (candidate) {
    var presentation = candidate.presentation;
    return !(isRecord(presentation) &&
        UNSUITABLE_MEMBER_FLAGS.some(function (flag) { return presentation[flag] === true; }));
};
/** The candidates of a contest the slate `slateId` can list. */
export var slateCandidateChoices = function (candidates, contestId, config, slateId) {
    var _a;
    var onOtherSlates = new Set(((_a = config === null || config === void 0 ? void 0 : config.slates) !== null && _a !== void 0 ? _a : [])
        .filter(function (_a) {
        var id = _a.id;
        return id !== slateId;
    })
        .flatMap(function (slate) { var _a; return (_a = slate.members[contestId]) !== null && _a !== void 0 ? _a : []; }));
    return candidates.filter(function (candidate) {
        return candidate.contest_id === contestId &&
            isEligibleSlateMember(candidate) &&
            !onOtherSlates.has(candidate.id);
    });
};
/** The form values of a slate: one picker per seat of each contest. */
export var slateFormValues = function (slate, contests, defaultLanguage) {
    var _a, _b, _c, _d, _e;
    var members = {};
    var _loop_1 = function (contest) {
        var current = (_a = slate === null || slate === void 0 ? void 0 : slate.members[contest.id]) !== null && _a !== void 0 ? _a : [];
        var seats = Math.max((_b = contest.max_votes) !== null && _b !== void 0 ? _b : 1, current.length, 1);
        members[contest.id] = Array.from({ length: seats }, function (_, index) { var _a; return (_a = current[index]) !== null && _a !== void 0 ? _a : ""; });
    };
    for (var _i = 0, contests_1 = contests; _i < contests_1.length; _i++) {
        var contest = contests_1[_i];
        _loop_1(contest);
    }
    return {
        name: slate ? getSlateName(slate, defaultLanguage, defaultLanguage) : "",
        alias: (_c = slate === null || slate === void 0 ? void 0 : slate.alias) !== null && _c !== void 0 ? _c : "",
        description: (_d = slate === null || slate === void 0 ? void 0 : slate.description) !== null && _d !== void 0 ? _d : "",
        external_id: (_e = slate === null || slate === void 0 ? void 0 : slate.external_id) !== null && _e !== void 0 ? _e : "",
        members: members,
    };
};
/** The slate the form describes, keeping its names in other languages. */
export var slateFromForm = function (id, values, defaultLanguage, previous) {
    var _a;
    var _b, _c, _d, _e, _f, _g;
    var members = {};
    for (var _i = 0, _h = Object.entries((_b = values.members) !== null && _b !== void 0 ? _b : {}); _i < _h.length; _i++) {
        var _j = _h[_i], contestId = _j[0], candidateIds = _j[1];
        var chosen = Array.from(new Set((candidateIds !== null && candidateIds !== void 0 ? candidateIds : []).filter(Boolean)));
        if (chosen.length > 0) {
            members[contestId] = chosen;
        }
    }
    var slate = {
        id: id,
        name: __assign(__assign({}, ((_c = previous === null || previous === void 0 ? void 0 : previous.name) !== null && _c !== void 0 ? _c : {})), (_a = {}, _a[defaultLanguage] = ((_d = values.name) !== null && _d !== void 0 ? _d : "").trim(), _a)),
        members: members,
    };
    var alias = ((_e = values.alias) !== null && _e !== void 0 ? _e : "").trim();
    var description = ((_f = values.description) !== null && _f !== void 0 ? _f : "").trim();
    var externalId = ((_g = values.external_id) !== null && _g !== void 0 ? _g : "").trim();
    if (alias) {
        slate.alias = alias;
    }
    if (description) {
        slate.description = description;
    }
    if (externalId) {
        slate.external_id = externalId;
    }
    return slate;
};
/** The problems of the slate configuration as the server will check it. */
export var checkSlatesConfiguration = function (text, defaultLanguage, contests, candidates) {
    var problems = check_election_slates_js(text, defaultLanguage, JSON.stringify(contests), JSON.stringify(candidates));
    if (!isProblemList(problems)) {
        throw new Error("Unexpected answer checking the slate configuration");
    }
    return problems;
};
/** The problems a thrown slate check carries, when that is what was thrown. */
export var slatesProblemsFromError = function (error) {
    return isProblemList(error) ? error : undefined;
};
export var formatSlatesProblems = function (problems) {
    return problems.map(function (problem) { return "".concat(problem.path, ": ").concat(problem.message); }).join("\n");
};
